import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
} from "@mui/material";
import {
  McButton,
  McInput,
  McSelect,
  McOption,
  McTag,
  McIcon
} from "@maersk-global/mds-react-wrapper";
import { McStepIndicator } from "@maersk-global/mds-react-wrapper/components-core/mc-step-indicator";
import { McRadio } from "@maersk-global/mds-react-wrapper/components-core/mc-radio";
import { McCheckbox } from "@maersk-global/mds-react-wrapper/components-core/mc-checkbox";
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/components-core/mc-loading-indicator";
import axios from "axios";
import { TableContext } from "../../context";
import { toast } from 'react-toastify';
import SetGroupDrawer from "./SetGroupDrawer";
import RapidHealthCheck from "./RapidHealthCheck";

/**
 * OnboardingWizard component handles the step-by-step application onboarding process
 * 
 * @param {Function} onClose - Handler to close the wizard
 * @returns {JSX.Element} The rendered onboarding wizard
 */
const OnboardingWizard = ({ onClose }) => {
  const { graphData } = useContext(TableContext);
  
  // Define the Maersk font family
  const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';
  
  const [currentStep, setCurrentStep] = useState(0);
  const [processType, setProcessType] = useState(null);
  const [isLoadingApps, setIsLoadingApps] = useState(false);
  const [availableApps, setAvailableApps] = useState([]);
  const [appsFetchStatus, setAppsFetchStatus] = useState(null);
  const [appOnboardingDetails, setAppOnboardingDetails] = useState({});
  const [isLoadingHealthCheck, setIsLoadingHealthCheck] = useState(false);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [isLoadingOnboardingStatus, setIsLoadingOnboardingStatus] = useState(false);
  const [healthCheckData, setHealthCheckData] = useState(null);
  const [onboardingStatusData, setOnboardingStatusData] = useState(null);
  const [isConfigureDrawerOpen, setIsConfigureDrawerOpen] = useState(false);
  const [configureType, setConfigureType] = useState(''); // 'approval_group' or 'support_group'
  const [isRapidConfigured, setIsRapidConfigured] = useState(false);
  const [groupData, setGroupData] = useState({
    change_control: { displayValue: '', value: '', previousvalue: '' },
    support_group: { displayValue: '', value: '', previousvalue: '' }
  });
  const [formData, setFormData] = useState({
    appId: '',
    appName: '',
    deploymentMethod: '',
    automationMaturity: '',
    riskHistory: '',
    applicationCategory: '',
    githubRepos: [''],
    fluxDeploymentVisibility: false,
    servicenowValidation: {
      cmdbExists: false,
      approvalGroups: false,
      assignmentGroups: false
    },
    engineeringApproval: 'pending',
    changeManagerApproval: 'pending'
  });

  // Step definitions
  const steps = [
    'App Selection',
    'Process Assessment', 
    'Health Check',
    'Request Submitted'
  ];

  // Fetch available applications for onboarding
  const fetchOnboardingApplications = async () => {
    try {
      setIsLoadingApps(true);
      setAppsFetchStatus(null);
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/get_onboarding_applications`,
        {
          user_email: graphData.mail,
          // user_email: graphData.mail.replace('@maersk.com', '@maersktest.com'),
          user_name: graphData.displayName,
          // user_email: "bipeen.kulkarni@maersktest.com",
          // user_name: "Bipeen Kulkarni"
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 404) {
        // Case 3: No applications owned by user
        setAvailableApps([]);
        setAppsFetchStatus('no_ownership');
        return;
      }

      if (response.status === 200) {
        const result = response.data;
        
        if (result.status === 200 && result.data && result.data.user_applications) {
          if (result.data.user_applications.length === 0) {
            // Case 2: All applications owned by user are onboarded already
            setAvailableApps([]);
            setAppsFetchStatus('all_onboarded');
          } else {
            // Case 1: Applications available for onboarding
            setAvailableApps(result.data.user_applications);
            setAppsFetchStatus('success');
          }
        }
      } else {
        // Case 4: Any other HTTP error
        console.error("Failed to fetch onboarding applications - HTTP status:", response.status);
        setAvailableApps([]);
        setAppsFetchStatus('no_ownership');
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching onboarding applications:", error);
      // if (appsFetchStatus !== 'no_ownership') {
      //   // Only show error toast for actual errors, not for no ownership case
      //   toast.error("Failed to load applications for onboarding", {
      //     position: "top-center",
      //     autoClose: 3000,
      //     hideProgressBar: false,
      //     closeOnClick: true,
      //     pauseOnHover: true,
      //     draggable: true,
      //     progress: undefined,
      //     theme: "light"
      //   });
      // }
      setAvailableApps([]);
      if (!appsFetchStatus) {
        setAppsFetchStatus('no_ownership');
      }
    } finally {
      setIsLoadingApps(false);
    }
  };

  // Load applications on component mount
  useEffect(() => {
    fetchOnboardingApplications();
  }, []);

  // Fetch readiness health check data
  const fetchReadinessHealthCheck = async () => {
    try {
      setIsLoadingHealthCheck(true);
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      if (!formData.appName) {
        throw new Error("Application name not available");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/readiness_health_check`,
        {
          application_id: formData.appId,
          application_instances: formData.appName
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;

        const nestedHealthData = result?.data?.data;
        const directHealthData = result?.data;
        const healthData = (result?.status === 200 && nestedHealthData)
          ? nestedHealthData
          : (result?.status === 200 && directHealthData ? directHealthData : null);
        const certificationData = result?.certification_data || result?.data?.certification_data;

        if (healthData) {
          setHealthCheckData({
            ...healthData,
            certification_data: certificationData || null
          });
          
          // Update appOnboardingDetails with health check results
          setAppOnboardingDetails(prev => ({
            ...prev,
            onboarding_details: {
              ...prev.onboarding_details,
              exists_in_snow_status: healthData.all_cis_exist_in_snow === 'yes' ? 'Yes' : 'No',
              approval_group: healthData.approval_groups || [],
              support_group: healthData.support_groups || [],
              certification_percentage: certificationData?.certification_percentage || null,
              certification_message: certificationData?.message || ''
            }
          }));
        } else {
          console.error("Unexpected health check response format:", result);
          setHealthCheckData(null);
        }
      } else {
        console.error("Failed to fetch health check - HTTP status:", response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching health check:", error);
      toast.error("Failed to load readiness health check", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      setHealthCheckData(null);
    } finally {
      setIsLoadingHealthCheck(false);
    }
  };

  // Submit onboarding request
  const submitOnboardingRequest = async () => {
    try {
      setIsSubmittingRequest(true);
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      if (!appOnboardingDetails || !appOnboardingDetails.onboarding_details) {
        throw new Error("Onboarding details not available");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/store_onboarding_request`,
        {
          application_id: appOnboardingDetails.application_id,
          onboarding_details: appOnboardingDetails.onboarding_details
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        if (result.status === 200) {
          toast.success("Onboarding request submitted successfully", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light"
          });
          // Move to the request submitted page
          setCurrentStep(3);
        } else {
          console.error("Unexpected response format:", result);
          throw new Error(result.message || "Failed to submit onboarding request");
        }
      } else {
        console.error("Failed to submit onboarding request - HTTP status:", response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error submitting onboarding request:", error);
      toast.error("Failed to submit onboarding request", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  // Fetch onboarding request status
  const fetchOnboardingRequestStatus = async () => {
    try {
      setIsLoadingOnboardingStatus(true);
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      if (!appOnboardingDetails || !appOnboardingDetails.application_id) {
        throw new Error("Application details not available");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/get_onboarding_request_status`,
        {
          application_id: appOnboardingDetails.application_id,
          user_name: graphData.displayName,
          owner_names: appOnboardingDetails.onboarding_details?.owner_names || []
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        if (result.status === 200 && result.data) {
          setOnboardingStatusData(result.data);
        } else {
          console.error("Unexpected response format:", result);
          setOnboardingStatusData(null);
        }
      } else {
        console.error("Failed to fetch onboarding status - HTTP status:", response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching onboarding status:", error);
      toast.error("Failed to load onboarding request status", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      setOnboardingStatusData(null);
    } finally {
      setIsLoadingOnboardingStatus(false);
    }
  };

  // Load health check when entering classic health check step
  useEffect(() => {
    if (currentStep === 2 && processType === 'classic' && formData.appName) {
      fetchReadinessHealthCheck();
    }
  }, [currentStep, processType, formData.appName]);

  // Load onboarding status when entering request submitted step
  useEffect(() => {
    if (currentStep === 3 && appOnboardingDetails.application_id) {
      fetchOnboardingRequestStatus();
    }
  }, [currentStep, appOnboardingDetails.application_id]);

  // Helper function to map form values to display values
  const mapAssessmentValues = () => {
    const deploymentMethodMap = {
      'automated': 'Automated CI/CD',
      'manual': 'Manual Process'
    };

    const automationMaturityMap = {
      'yes': 'High (Rollback + Testing)',
      'no': 'Limited'
    };

    const riskHistoryMap = {
      'yes': 'Has Incidents/Breaches',
      'no': 'Clean (No Incidents)'
    };

    const applicationTypeMap = {
      'infrastructure': 'Infrastructure Change',
      'legacy': 'Legacy App',
      'modern': 'Modern App'
    };

    return {
      deployment_method: deploymentMethodMap[formData.deploymentMethod] || '',
      automation_maturity: automationMaturityMap[formData.automationMaturity] || '',
      risk_history: riskHistoryMap[formData.riskHistory] || '',
      application_type: applicationTypeMap[formData.applicationCategory] || ''
    };
  };

  // Helper function to extract owner names
  const getOwnerNames = (owners) => {
    if (!owners) return [];
    const names = [];
    if (owners.service_owner) names.push(owners.service_owner);
    if (owners.senior_service_owner) names.push(owners.senior_service_owner);
    if (owners.engineering_manager) names.push(owners.engineering_manager);
    if (owners.senior_engineering_manager) names.push(owners.senior_engineering_manager);
    if (owners.engineering_director) names.push(owners.engineering_director);
    return [...new Set(names)]; // Remove duplicates
  };

  // Handle opening configure drawer
  const handleConfigureClick = (type) => {
    setConfigureType(type);
    // Initialize group data with current values from health check if available
    const currentApprovalGroup = healthCheckData?.approval_groups?.join(', ') || '';
    const currentSupportGroup = healthCheckData?.support_groups?.join(', ') || '';
    
    setGroupData({
      change_control: { 
        displayValue: currentApprovalGroup, 
        value: '', 
        previousvalue: currentApprovalGroup 
      },
      support_group: { 
        displayValue: currentSupportGroup, 
        value: '', 
        previousvalue: currentSupportGroup 
      }
    });
    setIsConfigureDrawerOpen(true);
  };

  // Handle closing configure drawer
  const handleCloseConfigureDrawer = () => {
    setIsConfigureDrawerOpen(false);
    setConfigureType('');
  };

  // Handle save group configuration (dummy for now)
  const handleSaveGroupConfiguration = () => {
    // TODO: Implement actual save functionality
    setIsConfigureDrawerOpen(false);
  };

  const handleConfigureCertification = () => {
    const applicationId = appOnboardingDetails?.application_id || formData.appId;

    const msaUrl = `${process.env.REACT_APP_MSA_LINK}/asset_lookup?applicationId=${applicationId}`;
    if (typeof window !== 'undefined') {
      window.open(msaUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Process type determination logic
  const determineProcessType = () => {
    const hasAutomatedPipeline = formData.deploymentMethod === 'automated';
    const hasMaturity = formData.automationMaturity === 'yes';
    const hasNoRiskHistory = formData.riskHistory === 'no';
    const isModernApp = formData.applicationCategory === 'modern';

    if (hasAutomatedPipeline && hasMaturity && hasNoRiskHistory && isModernApp) {
      setProcessType('rapid-green');
    } else {
      setProcessType('classic');
    }
  };

  // Auto-determine process type when all answers are provided
  useEffect(() => {
    if (formData.deploymentMethod && formData.automationMaturity && 
        formData.riskHistory && formData.applicationCategory) {
      determineProcessType();
    } else {
      setProcessType(null);
    }
  }, [formData.deploymentMethod, formData.automationMaturity, formData.riskHistory, formData.applicationCategory]);

  // Update appOnboardingDetails when processType changes
  useEffect(() => {
    if (processType && appOnboardingDetails.onboarding_details) {
      setAppOnboardingDetails(prev => ({
        ...prev,
        onboarding_details: {
          ...prev.onboarding_details,
          change_process_type: processType === 'rapid-green' ? 'rapid' : 'classic'
        }
      }));
    }
  }, [processType]);
  
  // Handle form data updates
  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Update appOnboardingDetails when assessment fields change
  useEffect(() => {
    if ((formData.deploymentMethod || formData.automationMaturity || 
        formData.riskHistory || formData.applicationCategory) && 
        appOnboardingDetails.onboarding_details) {
      
      const assessmentValues = mapAssessmentValues();
      
      setAppOnboardingDetails(prev => ({
        ...prev,
        onboarding_details: {
          ...prev.onboarding_details,
          change_assessment_summary: {
            deployment_method: assessmentValues.deployment_method,
            automation_maturity: assessmentValues.automation_maturity,
            risk_history: assessmentValues.risk_history,
            application_type: assessmentValues.application_type
          }
        }
      }));
    }
  }, [formData.deploymentMethod, formData.automationMaturity, formData.riskHistory, formData.applicationCategory]);

  // Navigation handlers
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleSubmitRequest = async () => {
    await submitOnboardingRequest();
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Validation logic for each step
  const canProceed = () => {
    switch (currentStep) {
      case 0: // App Selection
        return formData.appId; // Only need application ID selection
      case 1: // Process Assessment
        return formData.deploymentMethod && formData.automationMaturity && 
               formData.riskHistory && formData.applicationCategory;
      case 2: // Health Check
        if (processType === 'rapid-green') {
          // For rapid process, check if repositories are mapped and Gatekeeper integration is complete
          // console.log('Rapid Health Check Validation:', { isRapidConfigured, appOnboardingDetails });
          return isRapidConfigured;
        } else {
          // For classic process, all health checks must pass
          if (!healthCheckData) return false;
          const existsInSnow = healthCheckData.all_cis_exist_in_snow === 'yes';
          const isCertified = healthCheckData.certification_data?.certification_percentage === 100;
          const hasApprovalGroup = healthCheckData.approval_groups && healthCheckData.approval_groups.length > 0;
          const hasSupportGroup = healthCheckData.support_groups && healthCheckData.support_groups.length > 0;
          return isCertified && hasApprovalGroup && hasSupportGroup;
        }
      default:
        return true;
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', fontFamily: maerskFontFamily }}>
          Application Onboarding to Change Management
        </Typography>
        <Box sx={{
          px: 2,
          py: 1,
          border: '1px solid #d9d9d9',
          borderRadius: '10px'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
            Step {currentStep + 1} of {steps.length}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ m: 4 }}>
        <McStepIndicator
          labels={steps}
          currentindex={currentStep}
        />
      </Box>
    </Box>
  );

  // Render App Selection step
  const renderAppSelection = () => (
    <Card sx={{
      border: '1px solid #d9d9d9', 
      boxShadow: 'none',
      borderRadius: '10px' 
    }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontFamily: maerskFontFamily }}>Step 1: Onboarding Initiation</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: maerskFontFamily }}>
          Select the application you want to onboard to Change Management.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            {isLoadingApps ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: '10px' }}>
                <McIcon icon="arrow-clockwise" />
                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>Loading applications...</Typography>
              </Box>
            ) : availableApps.length === 0 ? (
              // Handle different empty states based on fetch status
              (() => {
                switch (appsFetchStatus) {
                  case 'all_onboarded':
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 3, border: '1px solid #e0e0e0', borderRadius: '10px', bgcolor: '#f0f8f0' }}>
                        <McIcon icon="check-circle" color="green" />
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                          All applications owned by user are already onboarded
                        </Typography>
                      </Box>
                    );
                  case 'no_ownership':
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 3, border: '1px solid #e0e0e0', borderRadius: '10px', bgcolor: '#fff8e1' }}>
                        <McIcon icon="exclamation-triangle" color="orange" />
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                          No applications owned by user
                        </Typography>
                      </Box>
                    );
                  // case 'error':
                  //   return (
                  //     <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 3, border: '1px solid #e0e0e0', borderRadius: '10px', bgcolor: '#ffebee' }}>
                  //       <McIcon icon="exclamation-triangle" color="red" />
                  //       <Typography variant="body2" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                  //         An error occurred while fetching applications for onboarding
                  //       </Typography>
                  //       <McButton variant="outlined" size="small" onClick={fetchOnboardingApplications}>
                  //         Retry Loading
                  //       </McButton>
                  //     </Box>
                  //   );
                  default:
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 3, border: '1px solid #e0e0e0', borderRadius: '10px', bgcolor: '#f5f5f5' }}>
                        <McIcon icon="exclamation-triangle" color="orange" />
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                          No applications available for onboarding for user
                        </Typography>
                      </Box>
                    );
                }
              })()
            ) : (
              <McSelect
                label="Select Application *"
                placeholder="Choose an application from dropdown"
                value={formData.appId}
                optionselected={(e) => {
                  const selectedApp = availableApps.find(app => app.application_id === e.detail.value);
                  if (selectedApp) {
                    // Get application instances names as array
                    const applicationNames = selectedApp.application_instances
                      ?.map(instance => instance.application_name)
                      .filter(name => name) || [];
                    
                    // Update form data with selected application
                    updateFormData('appId', e.detail.value);
                    updateFormData('appName', applicationNames);
                    
                    // Update app onboarding details
                    setAppOnboardingDetails({
                      application_id: e.detail.value,
                      onboarding_details: {
                        application_id: e.detail.value,
                        application_instances: applicationNames,
                        owner_names: selectedApp.application_owners || [],
                        requested_by: graphData.displayName,
                      }
                    });
                  }
                }}
                fit="medium"
                optionsheight="200px"
                style={{ width: '100%' }}
              >
                {availableApps.map(app => {
                  const applicationNames = app.application_instances
                    ?.map(instance => instance.application_name)
                    .filter(name => name)
                    .join(', ') || 'No instances';
                  
                  return (
                    <McOption key={app.application_id} value={app.application_id}>
                      <div style={{ fontSize: '14px' }}>
                        {app.application_id} - {applicationNames}
                      </div>
                    </McOption>
                  );
                })}
              </McSelect>
            )}
          </Box>

          {formData.appId && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                  Application Instances
                </Typography>
                
                <Box sx={{ 
                  p: 2, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '10px',
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontFamily: maerskFontFamily }}>
                    Application instances under this Application ID:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {(() => {
                      const selectedApp = availableApps.find(app => app.application_id === formData.appId);
                      const instances = selectedApp?.application_instances || [];
                      
                      if (instances.length === 0) {
                        return (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontFamily: maerskFontFamily }}>
                            No instances found
                          </Typography>
                        );
                      }
                      
                      return instances
                        .filter(instance => instance.application_name)
                        .map((instance, index) => (
                          <Box component="li" key={index} sx={{ mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                              {instance.application_name}
                            </Typography>
                          </Box>
                        ));
                    })()}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                  Application Owners (Auto-populated)
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <McInput
                    value={(() => {
                      const selectedApp = availableApps.find(app => app.application_id === formData.appId);
                      return selectedApp?.application_owners?.join(', ') || '';
                    })()}
                    fit="medium"
                    hiddenlabel
                    disabled
                    helpertext="All owners associated with this application ID"
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {formData.appId && (
          <Alert severity="info" sx={{ mt: 3, borderRadius: '10px' }}>
            <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
              Selected Application<br />
              <strong>Application ID: {formData.appId}</strong><br />
              <strong>Application Instances: {Array.isArray(formData.appName) ? formData.appName.join(', ') : formData.appName}</strong><br />
              will be onboarded to the Change Management process. 
              The following assessment will determine the appropriate change process pathway.
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  // Render Process Assessment step
  const renderProcessAssessment = () => (
    <Card sx={{
      border: '1px solid #d9d9d9', 
      boxShadow: 'none',
      borderRadius: '10px' 
    }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontFamily: maerskFontFamily }}>Step 2: Change Process Assessment</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: maerskFontFamily }}>
          Help us determine the most appropriate change management process for your application
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Deployment Method */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, fontFamily: maerskFontFamily }}>
              1. Deployment Method *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: maerskFontFamily }}>
              Do you deploy your application using an automated CI/CD pipeline (e.g., GitHub Actions, Jenkins, GitLab)?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <McRadio
                label="Yes - We have automated CI/CD pipelines for deployment"
                name="deploymentMethod"
                value="automated"
                checked={formData.deploymentMethod === 'automated'}
                change={(e) => updateFormData('deploymentMethod', 'automated')}
              />
              <McRadio
                label="No - We use manual deployment processes"
                name="deploymentMethod"
                value="manual"
                checked={formData.deploymentMethod === 'manual'}
                change={(e) => updateFormData('deploymentMethod', 'manual')}
              />
            </Box>
          </Box>

          {/* Automation Maturity */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, fontFamily: maerskFontFamily }}>
              2. Automation Maturity *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: maerskFontFamily }}>
              Does your pipeline support automated rollback or validated testing in lower environments?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <McRadio
                label="Yes - We have automated rollback and comprehensive testing"
                name="automationMaturity"
                value="yes"
                checked={formData.automationMaturity === 'yes'}
                change={(e) => updateFormData('automationMaturity', 'yes')}
              />
              <McRadio
                label="No - Limited automation or testing capabilities"
                name="automationMaturity"
                value="no"
                checked={formData.automationMaturity === 'no'}
                change={(e) => updateFormData('automationMaturity', 'no')}
              />
            </Box>
          </Box>

          {/* Risk History */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, fontFamily: maerskFontFamily }}>
              3. Application Risk History *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: maerskFontFamily }}>
              In the last 3 months, has your app had any P1/P2 incidents caused by deployments or SLO breaches?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <McRadio
                label="Yes - We've had P1/P2 incidents or SLO breaches"
                name="riskHistory"
                value="yes"
                checked={formData.riskHistory === 'yes'}
                change={(e) => updateFormData('riskHistory', 'yes')}
              />
              <McRadio
                label="No - Clean deployment history with no major incidents"
                name="riskHistory"
                value="no"
                checked={formData.riskHistory === 'no'}
                change={(e) => updateFormData('riskHistory', 'no')}
              />
            </Box>
          </Box>

          {/* Application Category */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, fontFamily: maerskFontFamily }}>
              4. Application Category *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: maerskFontFamily }}>
              Which category best describes your change activity?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <McRadio
                label="Infrastructure-wide / Horizontal changes (e.g., DB patching, shared platform upgrade)"
                name="applicationCategory"
                value="infrastructure"
                checked={formData.applicationCategory === 'infrastructure'}
                change={(e) => updateFormData('applicationCategory', 'infrastructure')}
              />
              <McRadio
                label="Legacy application with manual deployments"
                name="applicationCategory"
                value="legacy"
                checked={formData.applicationCategory === 'legacy'}
                change={(e) => updateFormData('applicationCategory', 'legacy')}
              />
              <McRadio
                label="Modern application with automated CI/CD"
                name="applicationCategory"
                value="modern"
                checked={formData.applicationCategory === 'modern'}
                change={(e) => updateFormData('applicationCategory', 'modern')}
              />
            </Box>
          </Box>
        </Box>

        {/* Process Recommendation */}
        {processType && (
          <Alert 
            severity={processType === 'rapid-green' ? 'success' : 'info'} 
            sx={{ mt: 4, borderRadius: '10px' }}
          >
            <Typography variant="subtitle2" sx={{ fontFamily: maerskFontFamily }}>
              Recommended Change Process
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontFamily: maerskFontFamily }}>
              <strong>
                {processType === 'rapid-green' ? 'CI/CD Automated Change Process (Green Channel)' : 'Manual Change Process'}
              </strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontFamily: maerskFontFamily }}>
              {processType === 'rapid-green' 
                ? '- Your application qualifies for the rapid change process with automated deployments and streamlined approvals. You\'ll start in the Green Channel with GitHub/Flux integration and automated workflows.'
                : '- Based on your responses, your application will follow the traditional change management process with enhanced controls, manual deployments, and ServiceNow integration. This ensures proper oversight for applications with higher risk profiles or manual processes.'
              }
            </Typography>
          </Alert>
        )}

        {/* Assessment Summary */}
        {processType && (
          <Box sx={{ mt: 3, borderRadius: '10px', backgroundColor: '#fbf9fa', p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontFamily: maerskFontFamily }}>
              Assessment Summary:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                  Deployment Method:
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'right', fontFamily: maerskFontFamily }}>
                  {formData.deploymentMethod === 'automated' ? 'Automated CI/CD' : 'Manual Process'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                  Automation Maturity:
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'right', fontFamily: maerskFontFamily }}>
                  {formData.automationMaturity === 'yes' ? 'High (Rollback + Testing)' : 'Limited Capabilities'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                  Risk History:
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'right', fontFamily: maerskFontFamily }}>
                  {formData.riskHistory === 'no' ? 'Clean (No incidents)' : 'Has P1/P2 Incidents'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                  Application Type:
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'right', fontFamily: maerskFontFamily }}>
                  {formData.applicationCategory === 'modern' ? 'Modern App' : 
                   formData.applicationCategory === 'legacy' ? 'Legacy App' : 
                   'Infrastructure Changes'}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  console.log("App Onboarding Details:", appOnboardingDetails);

  // Render Health Check step
  const renderHealthCheck = () => {
    if (processType === 'rapid-green') {
      return renderRapidHealthCheck();
    } else {
      return renderClassicHealthCheck();
    }
  };

  // Render Classic Health Check (Step 2A)
  const renderClassicHealthCheck = () => {
    // Show loading indicator while fetching health check data
    if (isLoadingHealthCheck) {
      return (
        <Card sx={{
          border: '1px solid #d9d9d9', 
          boxShadow: 'none',
          borderRadius: '10px' 
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <McIcon icon="database"/>
              <Typography variant="h6" sx={{ fontFamily: maerskFontFamily }}>
                Step 3A: Manual Change Process - Readiness Health Check
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: maerskFontFamily }}>
              System automatically checks readiness for manual deployments and infrastructure changes
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <McLoadingIndicator appearance="primary" />
            </Box>
          </CardContent>
        </Card>
      );
    }

    // If no health check data available, show error state
    if (!healthCheckData) {
      return (
        <Card sx={{
          border: '1px solid #d9d9d9', 
          boxShadow: 'none',
          borderRadius: '10px' 
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <McIcon icon="database"/>
              <Typography variant="h6" sx={{ fontFamily: maerskFontFamily }}>
                Step 3A: Manual Change Process - Readiness Health Check
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: maerskFontFamily }}>
              System automatically checks readiness for manual deployments and infrastructure changes
            </Typography>

            <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }}>
              <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                <strong>Health Check Failed</strong><br />
                Unable to load readiness assessment. Please try again.
              </Typography>
            </Alert>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <McButton variant="outlined" icon="arrow-clockwise" fit="small" onClick={fetchReadinessHealthCheck}>
                Re-run Readiness Check
              </McButton>
            </Box>
          </CardContent>
        </Card>
      );
    }

    // Calculate check results based on API data
    const existsInSnow = healthCheckData.all_cis_exist_in_snow === 'yes';
    const certificationData = healthCheckData?.certification_data;
    const isCertified = certificationData?.certification_percentage === 100;
    const certificationMessage = certificationData?.message || '';
    const hasApprovalGroup = healthCheckData.approval_groups && healthCheckData.approval_groups.length > 0;
    const hasSupportGroup = healthCheckData.support_groups && healthCheckData.support_groups.length > 0;
    const allChecksPass = isCertified && hasApprovalGroup && hasSupportGroup;

    return (
      <Card sx={{
        border: '1px solid #d9d9d9', 
        boxShadow: 'none',
        borderRadius: '10px' 
      }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <McIcon icon="database"/>
              <Typography variant="h6" sx={{ fontFamily: maerskFontFamily }}>
                Step 3A: Manual Change Process - Readiness Health Check
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: maerskFontFamily }}>
              System automatically checks readiness for manual deployments and infrastructure changes
            </Typography>          <Alert severity="info" sx={{ mb: 3, borderRadius: '10px' }}>
            <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
              <strong>Automatic Readiness Assessment</strong><br />
              System has automatically checked your application's readiness for Manual Change Process. Last checked: {new Date().toLocaleString()}
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* ServiceNow CMDB Check */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid #e0e0e0', borderRadius: '10px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <McIcon icon="database"/>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                    Application Instance(s) Certified in MSA
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                    {certificationMessage || 'The certification percentage of the Application in Maersk Service Atlas (MSA).'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <McTag 
                  label={isCertified ? "PASS" : "FAIL"} 
                  appearance={isCertified ? "success" : "error"} 
                />
                {!isCertified && (
                  <McButton 
                    variant="outlined" 
                    fit="small" 
                    icon="cog" 
                    onClick={handleConfigureCertification}
                  >
                    Certify
                  </McButton>
                )}
              </Box>
            </Box>

            {/* Approval Groups Check */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid #e0e0e0', borderRadius: '10px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <McIcon icon="users"/>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                    Approval Group assigned
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                    {hasApprovalGroup ? `Groups: ${healthCheckData.approval_groups.join(', ')}` : 'The group responsible for approving changes for the Application'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <McTag 
                  label={hasApprovalGroup ? "PASS" : "FAIL"} 
                  appearance={hasApprovalGroup ? "success" : "error"} 
                />
                {!hasApprovalGroup && (
                  <McButton variant="outlined" fit="small" icon="cog" onClick={() => handleConfigureClick('approval_group')}>
                    Configure
                  </McButton>
                )}
              </Box>
            </Box>

            {/* Support Groups Check */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid #e0e0e0', borderRadius: '10px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <McIcon icon="cog"/>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                    Support Group assigned
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                    {hasSupportGroup ? `Groups: ${healthCheckData.support_groups.join(', ')}` : 'The first line Application Support group'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <McTag 
                  label={hasSupportGroup ? "PASS" : "FAIL"} 
                  appearance={hasSupportGroup ? "success" : "error"} 
                />
                {!hasSupportGroup && (
                  <McButton variant="outlined" fit="small" icon="cog" onClick={() => handleConfigureClick('support_group')}>
                    Configure
                  </McButton>
                )}
              </Box>
            </Box>
          </Box>

          {/* Overall Status */}
          <Alert severity={allChecksPass ? 'success' : 'warning'} sx={{ borderRadius: '10px' }}>
            <Typography variant="subtitle2" sx={{ fontFamily: maerskFontFamily }}>
              Overall Readiness Status
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, fontFamily: maerskFontFamily }}>
              {allChecksPass ? 'READY' : 'NOT READY'}
            </Typography>
            {!allChecksPass && (
              <Typography variant="body2" sx={{ mt: 1, fontFamily: maerskFontFamily }}>
                - Please fix the failing configurations using the Configure buttons above. All validations must pass before proceeding to the approval workflow.
              </Typography>
            )}
          </Alert>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <McButton 
              variant="outlined" 
              icon="arrow-clockwise" 
              fit="small" 
              onClick={fetchReadinessHealthCheck}
              loading={isLoadingHealthCheck}
            >
              Re-run Readiness Check
            </McButton>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Render Rapid Health Check (Step 2B)
  const renderRapidHealthCheck = () => {
    return (
      <RapidHealthCheck
        formData={formData}
        updateFormData={updateFormData}
        appOnboardingData={appOnboardingDetails}
        setAppOnboardingData={setAppOnboardingDetails}
        onConfigurationChange={setIsRapidConfigured}
      />
    );
  };

  // Render Request Submitted step (combines approval status and completion message)
  const renderRequestSubmitted = () => {
    // Show loading indicator while fetching status data
    if (isLoadingOnboardingStatus) {
      return (
        <Card sx={{
          border: '1px solid #d9d9d9', 
          boxShadow: 'none',
          borderRadius: '10px' 
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontFamily: maerskFontFamily }}>Onboarding Request Submitted</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                  Loading your onboarding request status...
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <McLoadingIndicator appearance="primary" />
            </Box>
          </CardContent>
        </Card>
      );
    }

    // Get approval statuses from API data
    const engineeringApproval = onboardingStatusData?.engineering_team_approval || { status: 'pending' };
    const changeManagerApproval = onboardingStatusData?.change_manager_approval || { status: 'pending' };
    const onboardingDetails = onboardingStatusData?.onboarding_details || {};

    // Get icon color for status
    const getIconColor = (status) => {
      switch (status) {
        case 'approved': return 'green';
        case 'rejected': return 'red';
        case 'pending': 
        default: return 'orange';
      }
    };

    // Get status icon
    const getStatusIcon = (status) => {
      switch (status) {
        case 'approved': return 'check-circle';
        case 'rejected': return 'x-circle';
        case 'pending':
        default: return 'clock';
      }
    };

    // Determine overall status
    const overallStatus = (engineeringApproval.status === 'approved' && changeManagerApproval.status === 'approved') 
      ? 'approved' 
      : (engineeringApproval.status === 'rejected' || changeManagerApproval.status === 'rejected')
      ? 'rejected'
      : 'pending';

    return (
      <Card sx={{
        border: '1px solid #d9d9d9', 
        boxShadow: 'none',
        borderRadius: '10px' 
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontFamily: maerskFontFamily }}>Onboarding Request Submitted</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                Your application onboarding request has been submitted successfully
              </Typography>
            </Box>
          </Box>

          <Alert severity="success" sx={{ mb: 3, borderRadius: '10px' }}>
            <Typography variant="subtitle2" sx={{ fontFamily: maerskFontFamily }}>
              Request Submitted Successfully
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontFamily: maerskFontFamily }}>
              <strong>{Array.isArray(formData.appName) ? formData.appName.join(', ') : formData.appName}</strong> ({formData.appId}) has been submitted for onboarding to the{' '}
              <strong>{processType === 'rapid-green' ? 'Automated Green Channel' : 'Manual Change Process'}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontFamily: maerskFontFamily }}>
              - Your request is now being reviewed by the appropriate teams.
            </Typography>
            {onboardingDetails.request_id && (
              <Typography variant="body2" sx={{ mt: 1, fontFamily: maerskFontFamily }}>
                Request ID: <strong>{onboardingDetails.request_id}</strong>
              </Typography>
            )}
          </Alert>

          <Typography variant="h6" sx={{ mb: 3, fontFamily: maerskFontFamily }}>
            Current Approval Status
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
            {/* Engineering Team Approval */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid #e0e0e0', borderRadius: '10px' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                  Engineering Team Approval
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                  Technical review and sign-off
                </Typography>
                {engineeringApproval.approved_by && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontFamily: maerskFontFamily }}>
                    Approved by: {engineeringApproval.approved_by}
                  </Typography>
                )}
                {engineeringApproval.approval_date && (
                  <Typography variant="caption" sx={{ display: 'block', fontFamily: maerskFontFamily }}>
                    Date: {new Date(engineeringApproval.approval_date).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: '200px', justifyContent: 'flex-end' }}>
                <McIcon 
                  icon={getStatusIcon(engineeringApproval.status)}
                  color={getIconColor(engineeringApproval.status)}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, color: getIconColor(engineeringApproval.status), textTransform: 'capitalize', fontFamily: maerskFontFamily }}>
                  {engineeringApproval.status}
                </Typography>
              </Box>
            </Box>

            {/* Change Manager Approval */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid #e0e0e0', borderRadius: '10px' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                  Change Manager Approval
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                  Change management oversight and approval
                </Typography>
                {changeManagerApproval.approved_by && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontFamily: maerskFontFamily }}>
                    Approved by: {changeManagerApproval.approved_by}
                  </Typography>
                )}
                {changeManagerApproval.approval_date && (
                  <Typography variant="caption" sx={{ display: 'block', fontFamily: maerskFontFamily }}>
                    Date: {new Date(changeManagerApproval.approval_date).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: '200px', justifyContent: 'flex-end' }}>
                <McIcon 
                  icon={getStatusIcon(changeManagerApproval.status)}
                  color={getIconColor(changeManagerApproval.status)}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, color: getIconColor(changeManagerApproval.status), textTransform: 'capitalize', fontFamily: maerskFontFamily }}>
                  {changeManagerApproval.status}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Alert 
            severity={overallStatus === 'approved' ? 'success' : overallStatus === 'rejected' ? 'error' : 'warning'} 
            sx={{ mb: 3, borderRadius: '10px' }}
          >
            <Typography variant="subtitle2" sx={{ fontFamily: maerskFontFamily }}>
              Overall Approval Status
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, textTransform: 'uppercase', fontFamily: maerskFontFamily }}>
              {overallStatus}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontFamily: maerskFontFamily }}>
              {overallStatus === 'approved' 
                ? '- All required approvals have been obtained. Your application is ready for onboarding completion.'
                : overallStatus === 'rejected'
                ? '- One or more approvals have been rejected. Please review the feedback and resubmit if necessary.'
                : '- Waiting for all required approvals before proceeding to completion.'
              }
            </Typography>
          </Alert>

          <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: maerskFontFamily }}>
            Request Summary:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <McIcon color="green" icon="check-circle" />
              <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>Application details submitted</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <McIcon color="green" icon="check-circle" />
              <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>Process type determined and configured</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <McIcon color="green" icon="check-circle" />
              <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>ServiceNow/Repository integration configured</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <McIcon 
                color={overallStatus === 'approved' ? 'green' : 'orange'} 
                icon={overallStatus === 'approved' ? 'check-circle' : 'clock'} 
              />
              <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                {overallStatus === 'approved' ? 'All approvals obtained' : 'Awaiting approval(s)'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <McButton variant="filled" onClick={onClose}>
              Return to Dashboard
            </McButton>
            <McButton variant="outlined" onClick={fetchOnboardingRequestStatus}>
              Refresh Status
            </McButton>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 3 }}>
      {renderStepIndicator()}

      {currentStep === 0 && renderAppSelection()}
      {currentStep === 1 && renderProcessAssessment()}
      {currentStep === 2 && renderHealthCheck()}
      {currentStep === 3 && renderRequestSubmitted()}

      {/* Navigation */}
      {currentStep !== 3 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <McButton 
            variant="outlined" 
            onClick={currentStep === 0 ? onClose : handlePrevious}
            disabled={currentStep === 2 && (canProceed() || isSubmittingRequest)}
            icon={currentStep === 0 ? "chevron-left" : "arrow-left" }
          >
            {currentStep === 0 ? 'Back to Dashboard' : 'Previous'}
          </McButton>
          
          <McButton 
            variant="filled"
            onClick={currentStep === 2 ? handleSubmitRequest : handleNext}
            disabled={!canProceed() || isSubmittingRequest}
            loading={currentStep === 2 && isSubmittingRequest}
          >
            {currentStep === 2 ? 'Submit Request' : 'Next'}
            <span slot="trailingicon">
              <mc-icon icon={currentStep === 2 ? "paper-plane" : "arrow-right"} color="white"></mc-icon>
            </span>
          </McButton>
        </Box>
      )}

      {/* Configure Groups Drawer */}
      <SetGroupDrawer
        isOpen={isConfigureDrawerOpen}
        onClose={handleCloseConfigureDrawer}
        configureType={configureType}
        appId={formData.appId}
        appName={formData.appName}
        groupData={groupData}
        onGroupDataChange={setGroupData}
        onSave={handleSaveGroupConfiguration}
      />
    </Box>
  );
};

export default OnboardingWizard;
